import type { Passenger } from "../slices/passengerSlice";

/** Form labels from WEB-91 → Navitaire ISO + calling code. */
const RESIDENCE: Record<string, { iso: string; callingCode: string }> = {
  Colombia: { iso: "CO", callingCode: "57" },
  Chile: { iso: "CL", callingCode: "56" },
  Perú: { iso: "PE", callingCode: "51" },
  Peru: { iso: "PE", callingCode: "51" },
};

const DEFAULT_RESIDENCE = "Colombia";
const DEFAULT_PHONE_CALLING_CODE = "57";

type ContactPassenger = Pick<
  Passenger,
  "address" | "email" | "phone" | "callingCode" | "residenceCountry" | "countries"
>;

/** `Colombia (+57)` or `(+57)` → `57`. */
export function parsePhoneCallingCode(option?: string | null): string {
  if (!option) return "";
  const withPlus = option.match(/\(\+([0-9-]+)\)/);
  if (withPlus?.[1]) return withPlus[1].replace(/\D/g, "");
  const digits = option.trim();
  return /^\d{1,4}$/.test(digits) ? digits : "";
}

function isColombiaRoute(passenger: ContactPassenger) {
  return (
    passenger.countries?.departureCountry === "Colombia" ||
    passenger.countries?.destinationCountry === "Colombia"
  );
}

export function getPassengerNavitaireFields(passenger: ContactPassenger, cultureCode: string) {
  const lineOne = passenger.address?.trim() || "";
  const emailAddress = passenger.email?.trim() || "";
  const phoneDigits = passenger.phone?.trim() || "";
  const residenceLabel =
    passenger.residenceCountry || (isColombiaRoute(passenger) ? DEFAULT_RESIDENCE : undefined);
  const residence = residenceLabel ? RESIDENCE[residenceLabel] : undefined;
  const hasContact = Boolean(lineOne && emailAddress && phoneDigits);
  const callingCode =
    parsePhoneCallingCode(passenger.callingCode) ||
    residence?.callingCode ||
    DEFAULT_PHONE_CALLING_CODE;

  return {
    residenceCountryCode: residence?.iso ?? null,
    contactAddress: hasContact
      ? {
          status: 0,
          lineOne,
          emailAddress,
          phone: [callingCode, phoneDigits].filter(Boolean).join(" "),
          cultureCode,
          refusedContact: false,
          countryCode: residence?.iso,
        }
      : null,
  };
}

type StationCity = { stationCode?: string; cityCode?: string; code?: string };

export type RouteCountries = {
  departureCountry: string;
  destinationCountry: string;
};

export function getRouteCountriesFromStations(
  stations: Record<string, StationCity[]> | null | undefined,
  departureCityCode?: string,
  destinationCityCode?: string,
): RouteCountries | null {
  if (!stations || !departureCityCode || !destinationCityCode) return null;

  const countryFor = (cityCode: string) => {
    for (const [country, cities] of Object.entries(stations)) {
      if (
        cities.some(
          (city) =>
            city.stationCode === cityCode || city.cityCode === cityCode || city.code === cityCode,
        )
      ) {
        return country;
      }
    }
    return "";
  };

  const departureCountry = countryFor(departureCityCode);
  const destinationCountry = countryFor(destinationCityCode);
  if (!departureCountry && !destinationCountry) return null;
  return { departureCountry, destinationCountry };
}

export function routeCountriesFromPassenger(
  countries?: Passenger["countries"],
): RouteCountries | null {
  if (!countries?.departureCountry && !countries?.destinationCountry) return null;
  return {
    departureCountry: countries.departureCountry ?? "",
    destinationCountry: countries.destinationCountry ?? "",
  };
}
